import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Suspense } from "react";
import { PageLoader } from "@/components/PageLoader";
import { useState, useEffect, useRef, useCallback } from "react";
import {
    LayoutDashboard, Building2, Users, MessageSquareWarning,
    LogOut, Menu, X, ShieldCheck, DollarSign, ClipboardList, FileSignature,
    CalendarClock, CalendarDays, Wallet, Zap, Printer, FileCheck2, ScrollText, HeartPulse, Pill,
    ChevronDown, ChevronRight, UserCog, MessageSquare, Handshake, Activity, Stethoscope, FileStack, FileText, ShieldAlert, Scale, TrendingUp, CalendarOff,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import { AdminNotificationBell } from "@/components/AdminNotificationBell";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { api } from "@/lib/api";
import { StandingOrderReviewBanner } from "@/components/standing-orders/StandingOrderReviewBanner";

function AccountMenu({ user, onLogout }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const initials = (user?.full_name || user?.email || "A").trim().slice(0, 2).toUpperCase();
    const go = (path) => { setOpen(false); navigate(path); };
    const scope = user?.admin_scope;
    const isFullAdmin = !scope || scope === "Admin";
    return (
        <div className="relative">
            <button data-testid="admin-account-menu" onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)}
                className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-full bg-white border border-border/70 hover:border-phoenix-navy/40 transition-colors">
                <span className="w-7 h-7 rounded-full bg-phoenix-navy text-white text-[11px] font-bold flex items-center justify-center">{initials}</span>
                <span className="hidden sm:block text-sm font-medium text-phoenix-navy max-w-[140px] truncate">{user?.full_name || user?.email}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
            {open && (
                <div data-testid="admin-account-dropdown" className="absolute right-0 mt-2 w-60 bg-white border border-border/70 rounded-lg shadow-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-border/60">
                        <div className="text-sm font-semibold text-phoenix-navy truncate">{user?.full_name}</div>
                        <div className="text-xs text-muted-foreground truncate mt-0.5">{user?.email}</div>
                        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-phoenix-navy bg-phoenix-soft px-2 py-0.5 rounded">
                            <ShieldCheck className="w-3 h-3" /> {isFullAdmin ? "Platform Admin" : scope}
                        </div>
                    </div>
                    {isFullAdmin && (
                        <button data-testid="menu-corporate-contact" onMouseDown={() => go("/admin/corporate-contact")}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-phoenix-navy hover:bg-secondary/50 transition-colors">
                            <Building2 className="w-4 h-4" /> Corporate Contact
                        </button>
                    )}
                    {isFullAdmin && (
                        <button data-testid="menu-staff-permissions" onMouseDown={() => go("/admin/staff-permissions")}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-phoenix-navy hover:bg-secondary/50 transition-colors">
                            <UserCog className="w-4 h-4" /> Staff &amp; Permissions
                        </button>
                    )}
                    {isFullAdmin && (
                        <button data-testid="menu-directory" onMouseDown={() => go("/admin/directory")}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-phoenix-navy hover:bg-secondary/50 transition-colors">
                            <Users className="w-4 h-4" /> Directory
                        </button>
                    )}
                    <button data-testid="admin-logout" onMouseDown={onLogout}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors border-t border-border/60">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

const navItems = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", tid: "admin-nav-dashboard", exact: true },
    { to: "/admin/billing", icon: DollarSign, label: "Billing", tid: "admin-nav-billing" },
    { to: "/admin/partners", icon: Handshake, label: "Partner Payouts", tid: "admin-nav-partners" },
    { to: "/admin/messages", icon: MessageSquare, label: "Messages", tid: "admin-nav-messages", badge: "messages" },
    { to: "/admin/facilities", icon: Building2, label: "Facility", tid: "admin-nav-facilities", section: "Facilities", group: "fac" },
    { to: "/admin/statutes", icon: Scale, label: "Statute libraries", tid: "admin-nav-statutes", section: "Facilities", group: "fac" },
    { to: "/admin/renewals", icon: CalendarClock, label: "Renewal Center", tid: "admin-nav-renewals", group: "fac" },
    { to: "/admin/effectiveness", icon: TrendingUp, label: "Program Effectiveness", tid: "admin-nav-effectiveness", group: "fac" },
    { to: "/admin/pre-release", icon: CalendarClock, label: "Pre-Release", tid: "admin-nav-prerelease", group: "fac" },
    { to: "/admin/risk", icon: ShieldAlert, label: "Risk Assessment", tid: "admin-nav-risk", group: "fac",
        children: [{ to: "/admin/recidivism", label: "Recidivism Risk", tid: "admin-nav-recidivism" }] },
    { to: "/admin/app-engagement", icon: Activity, label: "App Engagement", tid: "admin-nav-app-engagement", group: "fac" },
    { to: "/admin/clinical-records", icon: Stethoscope, label: "Clinical Records", tid: "admin-nav-clinical-records", section: "Clinical", group: "clinical" },
    { to: "/admin/oncall", icon: CalendarClock, label: "On-Call Schedule", tid: "admin-nav-oncall", group: "clinical" },
    { to: "/admin/audit", icon: ScrollText, label: "Audit Log", tid: "admin-nav-audit", group: "clinical" },
    { to: "/admin/record-requests", icon: FileText, label: "Record Requests", tid: "admin-nav-record-requests", badge: "record-requests", group: "clinical" },
    { to: "/admin/templates", icon: FileStack, label: "Templates", tid: "admin-nav-templates", group: "clinical" },
    { to: "/admin/master-protocols", icon: ShieldCheck, label: "Med Director Protocols", tid: "admin-nav-master-protocols", group: "clinical" },
    { to: "/admin/encounter-templates", icon: FileStack, label: "Encounter Templates", tid: "admin-nav-encounter-templates", group: "clinical" },
    { to: "/admin/screening-templates", icon: ClipboardList, label: "Screening Templates", tid: "admin-nav-screening-templates", group: "clinical" },
    { to: "/admin/provider-management", icon: Users, label: "Provider Management", tid: "admin-nav-provider-mgmt", section: "Provider Management", group: "prov" },
    { to: "/admin/appointments", icon: CalendarDays, label: "Appointments", tid: "admin-nav-appointments", group: "prov" },
    { to: "/admin/medicaid", icon: HeartPulse, label: "Medicaid Oversight", tid: "admin-nav-medicaid", group: "prov" },
    { to: "/admin/applications", icon: ClipboardList, label: "Applications", tid: "admin-nav-applications", section: "HR & Payroll", group: "hr" },
    { to: "/admin/contracts", icon: FileSignature, label: "Contracts", tid: "admin-nav-contracts", group: "hr" },
    { to: "/admin/schedules", icon: CalendarClock, label: "Master Schedule", tid: "admin-nav-schedules", group: "hr" },
    { to: "/admin/payroll", icon: Wallet, label: "Payroll & Time", tid: "admin-nav-payroll", group: "hr" },
    { to: "/admin/automations", icon: Zap, label: "Automations", tid: "admin-nav-automations", section: "Platform", group: "platform" },
    { to: "/admin/fax", icon: Printer, label: "Fax Center", tid: "admin-nav-fax", group: "platform" },
    { to: "/admin/prescription-safety", icon: Pill, label: "Prescription Safety", tid: "admin-nav-prescription-safety", group: "platform" },
    { to: "/admin/user-consents", icon: ScrollText, label: "User Consents", tid: "admin-nav-user-consents", group: "platform" },
    { to: "/admin/consent-template", icon: FileCheck2, label: "Consent Templates", tid: "admin-nav-consent", group: "platform" },
    { to: "/admin/security", icon: ShieldCheck, label: "Security & Compliance", tid: "admin-nav-security", group: "platform" },
    { to: "/admin/coverage-board", icon: CalendarOff, label: "Coverage Board", tid: "admin-nav-coverage-board", group: "platform" },
];

// Platform-admin sub-role → visible sidebar items (full Admin sees everything).
const SCOPE_NAV = {
    "Messages": ["/admin/messages"],
    "HR & Payroll": ["/admin/applications", "/admin/contracts",
        "/admin/schedules", "/admin/appointments", "/admin/payroll", "/admin/messages"],
    "Billing": ["/admin/facilities", "/admin/billing", "/admin/medicaid", "/admin/messages"],
};

function AdminNavGroup({ item, onNavigate }) {
    const location = useLocation();
    const Icon = item.icon;
    const children = item.children || [];
    const childActive = children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
    const [open, setOpen] = useState(childActive);
    useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
    return (
        <div>
            <div className="flex items-center">
                <NavLink
                    to={item.to}
                    end={item.exact}
                    data-testid={item.tid}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                        `flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            isActive ? "bg-phoenix-navy text-white" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                        }`
                    }
                >
                    <Icon className="w-4 h-4" />
                    <span className="flex-1">{item.label}</span>
                </NavLink>
                <button
                    data-testid={`${item.tid}-toggle`}
                    onClick={() => setOpen((o) => !o)}
                    aria-label={`Toggle ${item.label}`}
                    className="ml-1 p-1.5 rounded-lg text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                >
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
            </div>
            {open && (
                <div className="ml-3 pl-3 border-l border-border/60 space-y-1 mt-1">
                    {children.map((c) => (
                        <NavLink
                            key={c.to}
                            to={c.to}
                            end={c.exact}
                            data-testid={c.tid}
                            onClick={onNavigate}
                            className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    isActive ? "bg-phoenix-navy text-white" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                                }`
                            }
                        >
                            {c.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [openSection, setOpenSection] = useState(null);
    const [flyoutTop, setFlyoutTop] = useState(0);
    const [flyoutLeft, setFlyoutLeft] = useState(256);
    const asideRef = useRef(null);
    const [onboardingCount, setOnboardingCount] = useState(0);
    const [escalationCount, setEscalationCount] = useState(0);
    const [seatRequestCount, setSeatRequestCount] = useState(0);
    const [recordReqCount, setRecordReqCount] = useState(0);
    const [payoutOpen, setPayoutOpen] = useState(0);
    const [payoutPastDue, setPayoutPastDue] = useState(0);
    const unread = useUnreadMessages();

    const scope = user?.admin_scope;
    const isFullAdmin = !scope || scope === "Admin";
    const visibleNav = isFullAdmin ? navItems : navItems.filter((n) => (SCOPE_NAV[scope] || []).includes(n.to));
    const firstAllowed = visibleNav[0]?.to || "/admin/messages";

    const SECTION_LABELS = { fac: "Facilities", clinical: "Clinical", prov: "Provider Management", hr: "HR & Payroll", platform: "Platform" };
    const SECTION_ORDER = ["fac", "clinical", "prov", "hr", "platform"];
    const topItems = visibleNav.filter((n) => !n.group);
    const sections = SECTION_ORDER
        .map((g) => ({ g, label: SECTION_LABELS[g], items: visibleNav.filter((n) => n.group === g) }))
        .filter((s) => s.items.length);

    const badgeFor = useCallback((n) => {
        if (n.to === "/admin/applications") return onboardingCount;
        if (n.to === "/admin/coverage") return escalationCount;
        if (n.to === "/admin/billing") return seatRequestCount;
        if (n.to === "/admin/record-requests") return recordReqCount;
        if (n.to === "/admin/partners") return payoutOpen;
        if (n.badge === "messages") return unread;
        return 0;
    }, [onboardingCount, escalationCount, seatRequestCount, recordReqCount, payoutOpen, unread]);

    const toggleSection = (g, e) => {
        if (openSection === g) { setOpenSection(null); return; }
        const r = e.currentTarget.getBoundingClientRect();
        const ar = asideRef.current?.getBoundingClientRect();
        setFlyoutTop(Math.max(8, Math.min(r.top, window.innerHeight - 360)));
        setFlyoutLeft((ar?.right ?? 256) + 4);
        setOpenSection(g);
    };

    useEffect(() => { setOpenSection(null); }, [location.pathname]);
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") setOpenSection(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const renderLink = (n) => {
        const { to, icon: Icon, label, tid, exact, indent } = n;
        if (n.children) {
            return <AdminNavGroup key={to} item={n} onNavigate={() => { setSidebarOpen(false); setOpenSection(null); }} />;
        }
        const count = badgeFor(n);
        const badgeColor = to === "/admin/coverage" || (to === "/admin/partners" && payoutPastDue > 0) ? "bg-red-600" : "bg-phoenix-orange";
        const badgeTid = to === "/admin/applications" ? "applications-onboarding-badge"
            : to === "/admin/coverage" ? "coverage-escalation-badge"
            : to === "/admin/billing" ? "seat-requests-badge"
            : to === "/admin/record-requests" ? "record-requests-badge"
            : to === "/admin/partners" ? "partner-payouts-badge"
            : n.badge === "messages" ? "admin-messages-badge" : undefined;
        return (
            <NavLink
                key={to}
                to={to}
                end={exact}
                data-testid={tid}
                onClick={() => { setSidebarOpen(false); setOpenSection(null); }}
                className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${indent ? "pl-9" : ""} ${
                        isActive ? "bg-phoenix-navy text-white" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                    }`
                }
            >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{label}</span>
                {count > 0 && badgeTid && (
                    <span data-testid={badgeTid}
                        className={`ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full ${badgeColor} text-white text-[11px] font-bold`}>
                        {count}
                    </span>
                )}
            </NavLink>
        );
    };

    // Scoped sub-roles may only view their allowed sections — bounce any other
    // admin route (including the dashboard) to their first allowed section.
    useEffect(() => {
        if (isFullAdmin) return;
        const allowed = SCOPE_NAV[scope] || [];
        const path = location.pathname;
        const ok = allowed.some((a) => path === a || path.startsWith(a + "/"));
        if (!ok) navigate(firstAllowed, { replace: true });
    }, [isFullAdmin, scope, location.pathname, firstAllowed, navigate]);

    useEffect(() => {
        let active = true;
        const fetchCount = () => api.get("/admin/hr/onboarding-count")
            .then((r) => { if (active) setOnboardingCount(r.data.total || 0); })
            .catch(() => {});
        const fetchEsc = () => api.get("/admin/coverage/escalation-count")
            .then((r) => { if (active) setEscalationCount(r.data.count || 0); })
            .catch(() => {});
        const fetchSeats = () => api.get("/admin/billing/seat-requests/count")
            .then((r) => { if (active) setSeatRequestCount(r.data.count || 0); })
            .catch(() => {});
        const fetchRecordReqs = () => api.get("/admin/record-requests/count")
            .then((r) => { if (active) setRecordReqCount(r.data.count || 0); })
            .catch(() => {});
        const fetchPayouts = () => api.get("/admin/partners/payouts/summary")
            .then((r) => { if (active) { setPayoutOpen(r.data.open_count || 0); setPayoutPastDue(r.data.past_due_count || 0); } })
            .catch(() => {});
        fetchCount(); fetchEsc(); fetchSeats(); fetchRecordReqs(); fetchPayouts();
        const id = setInterval(() => { fetchCount(); fetchEsc(); fetchSeats(); fetchRecordReqs(); fetchPayouts(); }, 60000);
        const onRefresh = () => { fetchCount(); fetchEsc(); };
        window.addEventListener("admin-counts-refresh", onRefresh);
        return () => { active = false; clearInterval(id); window.removeEventListener("admin-counts-refresh", onRefresh); };
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    return (
        <div className="min-h-screen flex bg-phoenix-bg">
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-black/40"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <aside
                ref={asideRef}
                data-testid="admin-sidebar"
                className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 bg-white border-r border-border/70 flex flex-col transform transition-transform duration-200 ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                } lg:translate-x-0`}
            >
                <div className="px-5 py-5 border-b border-border/70 flex items-center justify-between">
                    <PhoenixWordmark subtitle />
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden text-muted-foreground hover:text-phoenix-navy p-1"
                        aria-label="Close menu"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-5 py-3 border-b border-border/70 bg-phoenix-navy">
                    <div className="flex items-center gap-2 text-xs font-semibold text-white uppercase tracking-wider">
                        <ShieldCheck className="w-3.5 h-3.5" /> Platform Admin
                    </div>
                </div>
                <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                    {topItems.map(renderLink)}
                    {sections.length > 0 && <div className="pt-3" />}
                    {sections.map((s) => {
                        const sb = s.items.reduce((sum, n) => sum + badgeFor(n), 0);
                        const active = openSection === s.g;
                        return (
                            <button
                                key={s.g}
                                type="button"
                                data-testid={`admin-nav-group-toggle-${s.g}`}
                                onClick={(e) => toggleSection(s.g, e)}
                                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    active ? "bg-secondary/70 text-phoenix-navy" : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                                }`}
                            >
                                <span className="flex-1 text-left">{s.label}</span>
                                {sb > 0 && (
                                    <span data-testid={`admin-nav-group-badge-${s.g}`}
                                        className="min-w-[18px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-phoenix-orange text-white text-[10px] font-bold">
                                        {sb}
                                    </span>
                                )}
                                <ChevronRight className={`w-4 h-4 transition-transform ${active ? "rotate-90" : ""}`} />
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {openSection && (
                <>
                    <div className="fixed inset-0 z-[55]" onClick={() => setOpenSection(null)} data-testid="admin-nav-flyout-backdrop" />
                    <div
                        data-testid={`admin-nav-flyout-${openSection}`}
                        style={{ top: flyoutTop, left: flyoutLeft }}
                        className="fixed z-[60] w-60 max-h-[80vh] overflow-y-auto bg-white border border-border/70 rounded-xl shadow-xl p-2 space-y-1"
                    >
                        <div className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                            {sections.find((s) => s.g === openSection)?.label}
                        </div>
                        {sections.find((s) => s.g === openSection)?.items.map(renderLink)}
                    </div>
                </>
            )}

            <main className="flex-1 flex flex-col min-w-0">
                <div className="sticky top-0 z-30 bg-white border-b border-border/70 px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        data-testid="admin-sidebar-toggle"
                        className="lg:hidden p-2 -ml-2 rounded-lg text-phoenix-navy hover:bg-secondary/60 transition-colors"
                        aria-label="Open menu"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <PhoenixWordmark size={28} className="lg:hidden" />
                    <div className="ml-auto flex items-center gap-3">
                        <AdminNotificationBell />
                        <AccountMenu user={user} onLogout={handleLogout} />
                    </div>
                </div>
                <div className="flex-1 overflow-auto">
                    <StandingOrderReviewBanner />
                    <Suspense fallback={<PageLoader />}>
                        <Outlet />
                    </Suspense>
                </div>
            </main>
        </div>
    );
}
